import React from 'react';
import {Divider} from '@mui/material';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useHash} from '@netx/core-components/hooks/useHash';
import {isRouteMatch} from '@netx/core-next/utils/route';
import {type ReactAction} from '@netx/core/bootstrap';
import {
	ActionMenuListItem,
	type ActionMenuListItemProps,
} from './ActionMenuListItem';

export interface ActionMenuListProps {
	actions: Array<ReactAction | ReactAction[]>;
	disableIcons?: boolean;
	onAction: (event: React.MouseEvent<HTMLElement>, action: ReactAction) => void;
	MenuItemProps?:
		| Partial<ActionMenuListItemProps>
		| ((action: ReactAction) => Partial<ActionMenuListItemProps>);
}

export const renderActionMenuList = ({
	actions,
	disableIcons,
	onAction,
	MenuItemProps,
}: ActionMenuListProps) => {
	const hash = useHash();
	const renderAction = useConstCallback(
		(action: ReactAction, someSelected?: boolean) => {
			if (action.children != null) {
				if (!action.children.length) return null;
				const flattenedChildren = action.children?.flat();
				if (action.showChildWhenOnly && flattenedChildren.length === 1) {
					return renderAction(flattenedChildren[0], someSelected);
				}
			}

			const routeMatch = isRouteMatch(hash.currentRoute, action.routeMatch);
			return (
				<ActionMenuListItem
					key={`action-menu-item-${action.label}`}
					disableIcon={disableIcons}
					someSelected={someSelected}
					selected={routeMatch || action.selected}
					{...(typeof MenuItemProps === 'function'
						? MenuItemProps(action)
						: MenuItemProps)}
					action={action}
					onClick={(event) => onAction(event, action)}
				/>
			);
		},
	);

	const someSelected = actions.some((action) => {
		return Array.isArray(action) ? false : action.selected;
	});

	return actions.map((action, index) => {
		if (Array.isArray(action)) {
			const someSelected = actions.some((action) =>
				Array.isArray(action) ? false : action.selected,
			);
			return [
				index > 0 ? <Divider key={`divider-${index}`} /> : null,
				...action.map((action) => renderAction(action, someSelected)),
			];
		}
		return renderAction(action, someSelected);
	});
};

export const ActionMenuList = (props: ActionMenuListProps) => {
	return <>{renderActionMenuList(props)}</>;
};

export default ActionMenuList;
